import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useRef } from "react"
import {
	type SocialMatchStatus,
	skipSocialMatchFeedback,
	socialMatchKeys,
	submitSocialMatchFeedback,
} from "@/entities/social-match"
import { useCurrentUserId } from "@/entities/user"
import { captureError } from "@/shared/lib/diagnostics"

export function useSocialMatchFeedback() {
	const userId = useCurrentUserId()
	const queryClient = useQueryClient()
	const inFlight = useRef(false)

	const applyStatus = (status: SocialMatchStatus) => {
		queryClient.setQueryData(socialMatchKeys.status(userId), status)
	}

	const submitMutation = useMutation({
		mutationFn: submitSocialMatchFeedback,
		onSuccess: applyStatus,
		onError: (error) => captureError(error, { scope: "socialMatch.feedback" }),
	})

	const skipMutation = useMutation({
		mutationFn: skipSocialMatchFeedback,
		onSuccess: applyStatus,
		onError: (error) =>
			captureError(error, { scope: "socialMatch.feedbackSkip" }),
	})

	const isPending = submitMutation.isPending || skipMutation.isPending

	const submit = (
		args: { requestId: string; rating: number; comment: string },
		onDone: () => void,
	) => {
		if (inFlight.current) return
		inFlight.current = true
		submitMutation.mutate(args, {
			onSuccess: () => onDone(),
			onSettled: () => {
				inFlight.current = false
			},
		})
	}

	const skip = (requestId: string, onDone: () => void) => {
		if (inFlight.current) return
		inFlight.current = true
		skipMutation.mutate(requestId, {
			onSuccess: () => onDone(),
			onSettled: () => {
				inFlight.current = false
			},
		})
	}

	return {
		submit,
		skip,
		isPending,
		failed: submitMutation.isError || skipMutation.isError,
	}
}
